import React from 'react';
const { useState, useEffect, useMemo, useRef } = React;
import { TABLE_IDS, CURRENT_USER, getAuthHeaders } from '../../globals.js';
import { F, linkedIds, activateAccountIfNeeded } from '../../utils.jsx';
import { outreachKind } from '../../outreachEvidence.js';
import { ownerCall } from '../owner/shared.jsx';
import EmailComposeModal from '../EmailComposeModal.jsx';
import NextActionProposal from './NextActionProposal.jsx';
import { proposalFor, registrationFact, factOf, needsMotive } from '../../nextActionRules.js';
import { hasProposalRequest, withProposalMark } from '../../proposalRequest.js';
import { gmailCoverage } from '../../gmailSync.js';
import {
  OUTCOMES, CHANNELS, contactStatusAfter, activityFields, DUE_SHORTCUTS, dueFromShortcut, suggestedNext,
  closeProblem, stepsFor, runSteps, partialText, openContactTasks, ensureActivity,
} from '../../followupPanel.js';

// Mi espacio › Hoy · panel de seguimiento: open a contact over Hoy, see the context and the last interaction,
// contact, register what happened and leave the next step, without leaving Hoy (it stays mounted: its sections,
// filters and scroll are kept). Closing it saves nothing. Opening a channel or writing a note never completes a task:
// a planned action is marked done or rescheduled only with its own button (with its history). One «Guardar»
// coordinates the activity, the contact's state and the new next action; if one fails it says what was saved and a
// retry does only what is missing (the same operation id, so nothing is created twice). The «Propuesta de Oike»
// (NextActionProposal, rules in src/nextActionRules.js) suggests the next action and prepares its draft with the AI
// only from the data Ale authorized; confirming goes through the same «Guardar» steps.

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
const KIND = { inbound: 'Respuesta', gmail_sent: 'Correo enviado (verificado)', confirmed: 'Contacto confirmado', logged: 'Registrado a mano', opened: 'Canal abierto (no cuenta como contacto)', ignored: 'Borrador' };
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso || ''; } };
const fmtDay = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('es', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) : 'sin fecha');
const cleanNotes = (n) => String(n || '').replace(/\[(oike-confirmado|op:[^\]]+|gmsg:[^\]]+|gthread:[^\]]+|gmsgid:[^\]]+)\]\s*/g, '').trim();
const btn = { fontSize: 12, padding: '4px 10px' };
const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const section = { borderTop: '1px solid var(--globant-border)', padding: '12px 0' };
const head = { fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--globant-muted)', marginBottom: 6 };

export default function FollowupPanel({ stakeholderId, day, today, commercialData, commercialApi, onClose, onChanged, onOpenFull }) {
  const { stakeholders = [], accounts = [], outreach = [] } = commercialData || {};
  const s = stakeholders.find(x => x.id === stakeholderId);
  const accountIds = s ? linkedIds(s, 'Account') : [];
  const account = accounts.find(a => a.id === accountIds[0]);
  const name = s ? `${F(s, 'Name') || ''} ${F(s, 'Last name') || ''}`.trim() || 'Contacto' : 'Contacto';
  const status = s ? String(F(s, 'Status') || '').trim() : '';
  const history = useMemo(() => outreach.filter(o => linkedIds(o, 'Stakeholder').includes(stakeholderId)).sort((a, b) => new Date(F(b, 'Date') || 0) - new Date(F(a, 'Date') || 0)), [outreach, stakeholderId]);
  const openTasks = useMemo(() => openContactTasks(day, stakeholderId), [day, stakeholderId]);

  const op = useRef(uid());
  const lock = useRef(false);
  const [outcome, setOutcome] = useState('none');
  const [channel, setChannel] = useState('Email');
  const [note, setNote] = useState('');
  const [meetingAt, setMeetingAt] = useState('');
  const [next, setNext] = useState({ mode: '', title: '', dueOn: '' });
  const [showAll, setShowAll] = useState(false);
  const [compose, setCompose] = useState(false);
  const [gmail, setGmail] = useState(null);
  const [emailSent, setEmailSent] = useState(null); // { logged }
  const [saved, setSaved] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { tone, text }
  const [taskBusy, setTaskBusy] = useState('');
  const [taskMsg, setTaskMsg] = useState('');
  const [reschedulingId, setReschedulingId] = useState('');

  // Configuración's Gmail, checked by the server (never a flag of the browser)
  useEffect(() => { fetch('/api/gmail/status', { method: 'POST', headers: getAuthHeaders() }).then(r => r.json()).then(d => setGmail(!!d.connected)).catch(() => setGmail(false)); }, []);
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') tryClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); });

  const emailLogged = !!emailSent?.logged;
  // The selected answer: the latest interaction that counts is theirs. Registering «Respondió» again would duplicate it;
  // «Pidió una propuesta» marks that same answer instead of creating another one.
  const tz = day?.timeZone || 'UTC';
  const facts = useMemo(() => history.map(o => ({ o, f: factOf(o, tz) })).filter(x => x.f).sort((a, b) => String(a.f.date).localeCompare(String(b.f.date)) || String(a.f.at).localeCompare(String(b.f.at))), [history, tz]);
  const lastFact = facts[facts.length - 1] || null;
  const selectedReply = lastFact?.f.type === 'inbound' ? lastFact.o : null;
  const replyMarked = selectedReply ? hasProposalRequest(F(selectedReply, 'Notes')) : false;
  const statusAfter = contactStatusAfter(outcome, status) || status;
  const registration = registrationFact({ outcome, channel, op: op.current, today, meetingAt, tz, replyId: outcome === 'proposal' && selectedReply ? selectedReply.id : '' });
  // «Preparar primer contacto» (R0): the channel Ale picks; each channel keeps its own draft
  const [firstChannel, setFirstChannel] = useState('');
  const proposal = s ? proposalFor({
    stakeholderId, records: history, registration, status: statusAfter, today, tz, coverage: gmailCoverage(), firstChannel,
    contact: { name: `${F(s, 'Name') || ''}`.trim() || name, email: F(s, 'Email'), phone: F(s, 'Phone number') || F(s, 'Phone'), linkedin: F(s, 'LinkedIn') },
  }) : null;
  const proposalFacts = [...facts.map(x => x.f), ...(registration && !registration.markReply ? [registration] : [])];
  const duplicateEmail = emailLogged && outcome === 'contacted' && channel === 'Email';
  const interaction = outcome !== 'none' || !!emailSent;
  const dirty = outcome !== 'none' || note.trim() || next.mode || (emailSent && !saved.done);

  const tryClose = () => {
    if (busy) return;
    if (dirty && !saved.done && !window.confirm(emailSent ? 'El correo ya salió (y no se reenvía). ¿Cerrar sin guardar lo que pasó ni la próxima acción?' : '¿Cerrar sin guardar?')) return;
    onClose();
  };

  // An open action: marked done or rescheduled only here, explicitly (the same task, with its history)
  const markDone = async (t) => {
    setTaskBusy(t.id); setTaskMsg('');
    try { await ownerCall({ action: 'setTaskStatus', id: t.id, status: 'done', today }); await onChanged?.(); setTaskMsg(`«${t.title}» quedó realizada.`); }
    catch (e) { setTaskMsg(e.message); }
    setTaskBusy('');
  };
  const reschedule = async (t, dueOn) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueOn || '')) return;
    setTaskBusy(t.id); setTaskMsg('');
    try { await ownerCall({ action: 'rescheduleTask', id: t.id, dueOn, today }); await onChanged?.(); setReschedulingId(''); setTaskMsg(`«${t.title}» pasó al ${fmtDay(dueOn)}.`); }
    catch (e) { setTaskMsg(e.message); }
    setTaskBusy('');
  };

  const marking = outcome === 'proposal' && !!selectedReply;
  const plan = (nx) => ({
    activity: OUTCOMES.find(o => o.key === outcome)?.status && !duplicateEmail && !marking ? true : null,
    mark: marking && !replyMarked ? { replyId: selectedReply.id } : null,
    statusTo: contactStatusAfter(outcome, status),
    create: nx.mode === 'new' ? { title: nx.title.trim(), dueOn: nx.dueOn, notes: nx.notes || '', suggestion: nx.suggestion || null } : null,
    update: nx.mode === 'update' ? { taskId: nx.taskId, title: nx.title.trim(), dueOn: nx.dueOn, appendNotes: nx.appendNotes || '', expectUpdatedAt: nx.expectUpdatedAt || '', suggestion: nx.suggestion || null } : null,
  });

  // «Confirmar» of the proposal: the same «Guardar», with the proposal as the next step
  const confirmProposal = (nx) => { setNext(nx); save(nx); };

  const save = async (override) => {
    const nx = override && override.mode ? override : next;
    if (lock.current) return; // a second click while saving does nothing
    if (duplicateEmail) { setMsg({ tone: 'warn', text: 'El correo ya quedó registrado al enviarlo: no lo registres otra vez. Elegí otro resultado o «Solo consulté».' }); return; }
    if (outcome === 'replied' && selectedReply) { setMsg({ tone: 'warn', text: 'Esa respuesta ya está registrada: no hace falta registrar otra. Elegí «Solo consulté» o «Pidió una propuesta».' }); return; }
    const problem = closeProblem({ outcome, emailSent: !!emailSent, next: nx, openTasks });
    if (problem && !(saved.activity || saved.status || saved.create || saved.mark)) { setMsg({ tone: 'warn', text: problem }); return; }
    const api = commercialApi?.api;
    const p = plan(nx);
    if ((p.activity || p.statusTo || p.mark) && !api) { setMsg({ tone: 'warn', text: 'Los datos comerciales no están disponibles: no se guardó nada.' }); return; }
    const steps = stepsFor(p, saved);
    lock.current = true; setBusy(true); setMsg(null);
    const ops = {
      activity: async () => {
        const fields = activityFields({ outcome, channel, note, stakeholderId, accountIds, name, op: op.current, loggedBy: CURRENT_USER?.name || '', meetingAt: meetingAt ? new Date(meetingAt).toISOString() : null });
        const kept = await ensureActivity({
          op: op.current, fields,
          find: (o) => api.findByOp(TABLE_IDS.outreach, o),
          create: (f) => api.createRecord(TABLE_IDS.outreach, f),
          remove: (id) => api.deleteRecord(TABLE_IDS.outreach, id),
        });
        commercialApi.addToData?.('outreach', kept.fields, kept.id);
        return kept.id;
      },
      // «Pidió una propuesta» on the selected answer: only the mark, prepended (airtable-proxy validates it)
      mark: async (st) => {
        const rec = history.find(o => o.id === st.replyId);
        const notes = String(F(rec, 'Notes') || '');
        if (hasProposalRequest(notes)) return st.replyId;
        const next = withProposalMark(notes);
        await api.updateRecord(TABLE_IDS.outreach, st.replyId, { 'Notes': next });
        commercialApi.updateInData?.('outreach', st.replyId, { 'Notes': next });
        return st.replyId;
      },
      status: async (st) => {
        await api.updateRecord(TABLE_IDS.stakeholders, stakeholderId, { 'Status': st.to });
        commercialApi.updateInData?.('stakeholders', stakeholderId, { 'Status': st.to });
        activateAccountIfNeeded(api, accountIds, accounts).catch?.(() => {});
        return st.to;
      },
      create: async (st) => {
        try {
          const r = await ownerCall({ action: 'createContactTask', stakeholderId, accountId: accountIds[0] || null, title: st.title, dueOn: st.dueOn, op: op.current, expectOpen: openTasks.map(t => t.id), ...(st.notes ? { notes: st.notes } : {}), ...(st.suggestion ? { suggestion: st.suggestion } : {}) });
          return r.task?.id || true;
        } catch (e) { throw new Error(/changed/.test(e.code || '') ? 'contact_tasks_changed' : e.message); }
      },
      // «Actualizar esa acción»: the same open action (never another one), only if it did not change meanwhile
      update: async (st) => {
        try {
          const r = await ownerCall({ action: 'updateContactTask', id: st.taskId, stakeholderId, op: op.current, title: st.title, dueOn: st.dueOn, appendNotes: st.appendNotes, expectUpdatedAt: st.expectUpdatedAt, ...(st.suggestion ? { suggestion: st.suggestion } : {}) });
          return r.task?.id || true;
        } catch (e) { throw new Error(/changed/.test(e.code || '') ? 'contact_tasks_changed' : /notes_too_long/.test(e.code || '') ? 'las notas de la acción ya están llenas: desmarcá «Guardar el borrador en la tarea»' : e.message); }
      },
    };
    const r = await runSteps(steps, ops, saved);
    setSaved(r.saved);
    lock.current = false; setBusy(false);
    if (r.failed) {
      const text = /contact_tasks_changed/.test(r.failed.error) ? 'apareció otra próxima acción para este contacto mientras tanto: revisala arriba' : r.failed.error;
      setMsg({ tone: 'error', text: partialText(r.saved, { ...r.failed, error: text }, steps) });
      if (/contact_tasks_changed/.test(r.failed.error)) onChanged?.();
      return;
    }
    setSaved(x => ({ ...x, done: true }));
    await onChanged?.();
    commercialApi?.refresh?.();
    window.__oikeToast?.('Seguimiento guardado.', 'success');
    onClose();
  };

  const phone = s ? String(F(s, 'Phone number') || F(s, 'Phone') || '') : '';
  const email = s ? String(F(s, 'Email') || '') : '';
  const linkedin = s ? String(F(s, 'LinkedIn') || '') : '';
  const shown = showAll ? history : history.slice(0, 3);
  const retrying = !!(saved.activity || saved.status) && !saved.done;

  return (
    <div role="dialog" aria-modal="true" aria-label={`Seguimiento de ${name}`} onClick={tryClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(30,42,44,0.25)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(560px, 100vw)', height: '100%', overflowY: 'auto', background: 'var(--globant-card, #fff)', boxShadow: '-8px 0 24px rgba(0,0,0,0.12)', padding: '16px 18px 24px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, overflowWrap: 'anywhere' }}>{name}</div>
            <div style={muted}>{[s && F(s, 'Role'), account && F(account, 'Account Name')].filter(Boolean).join(' · ') || (s ? 'Sin empresa' : '')}{status ? ` · estado: ${status}` : ''}</div>
          </div>
          <button type="button" className="action-btn" style={btn} onClick={tryClose} aria-label="Cerrar">Cerrar</button>
        </div>
        {!s ? <div style={{ ...muted, marginTop: 12 }}>Este contacto no está en los datos comerciales cargados (puede ser un registro de prueba o haberse borrado). No se puede registrar nada desde aquí.</div> : <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0 4px' }}>
            <button type="button" className="action-btn" style={btn} onClick={() => onOpenFull?.(stakeholderId, accountIds[0], name)}>Ficha completa y cotizaciones</button>
            {account && <button type="button" className="action-btn" style={btn} onClick={() => onOpenFull?.(stakeholderId, accountIds[0], name, 'account')}>Ver empresa</button>}
          </div>

          <div style={section}>
            <div style={head}>Contexto</div>
            <div style={{ fontSize: 13, display: 'grid', gap: 2 }}>
              {email && <span>✉ {email}</span>}{phone && <span>☎ {phone}</span>}{linkedin && <span>🔗 LinkedIn</span>}
              {F(s, 'Notes') && <span style={muted}>Notas: {String(F(s, 'Notes')).slice(0, 220)}{String(F(s, 'Notes')).length > 220 ? '…' : ''}</span>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, margin: '8px 0 2px' }}>Última interacción</div>
            {!history.length ? <div style={muted}>Sin actividad registrada.</div> : shown.map(o => (
              <div key={o.id} style={{ fontSize: 12, padding: '3px 0' }}>
                <b>{fmt(F(o, 'Date'))}</b> · {KIND[outreachKind(o)] || F(o, 'Status')}{F(o, 'Channel') ? ` · ${F(o, 'Channel')}` : ''}{F(o, 'Logged By') ? ` · ${F(o, 'Logged By')}` : ''}
                {cleanNotes(F(o, 'Notes')) && <div style={{ ...muted, overflowWrap: 'anywhere' }}>{cleanNotes(F(o, 'Notes')).slice(0, 180)}</div>}
              </div>
            ))}
            {history.length > 3 && <button type="button" className="action-btn" style={{ ...btn, marginTop: 4 }} onClick={() => setShowAll(v => !v)}>{showAll ? 'Ver menos' : `Ver historial (${history.length})`}</button>}
          </div>

          <div style={section}>
            <div style={head}>Próxima acción planificada</div>
            {!openTasks.length ? <div style={muted}>Ninguna.</div> : <>
              {openTasks.length > 1 && <div style={{ fontSize: 12, color: '#9A6212', marginBottom: 4 }}>Hay {openTasks.length} acciones abiertas para este contacto. Oike no las une ni las cierra: marcá la que ya hiciste o reprogramala.</div>}
              {openTasks.map(t => (
                <div key={t.id} style={{ padding: '4px 0', fontSize: 13 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <span style={{ flex: '1 1 200px' }}><b>{t.title}</b> <span style={{ ...muted, color: t.dueOn && t.dueOn < today ? '#9A6212' : undefined }}>· {fmtDay(t.dueOn)}{t.dueOn && t.dueOn < today ? ' · vencida' : ''}{t.reschedules ? ` · reprogramada ${t.reschedules}×` : ''}</span></span>
                    <button type="button" className="action-btn" style={btn} disabled={taskBusy === t.id} onClick={() => markDone(t)}>Marcar realizada</button>
                    <button type="button" className="action-btn" style={btn} disabled={taskBusy === t.id} onClick={() => setReschedulingId(id => (id === t.id ? '' : t.id))}>Reprogramar</button>
                  </div>
                  {reschedulingId === t.id && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {DUE_SHORTCUTS.map(([k, l]) => <button key={k} type="button" className="action-btn" style={btn} disabled={taskBusy === t.id} onClick={() => reschedule(t, dueFromShortcut(k, today))}>{l}</button>)}
                      <input type="date" className="input-field" style={{ fontSize: 12, width: 'auto' }} min={today} onChange={e => e.target.value && reschedule(t, e.target.value)} aria-label="Nueva fecha" />
                    </div>
                  )}
                </div>
              ))}
            </>}
            {taskMsg && <div role="status" style={{ fontSize: 12, marginTop: 4 }}>{taskMsg}</div>}
          </div>

          {proposal?.rule === 'R0' && (
            <div style={section}>
              <NextActionProposal proposal={proposal} needMotive={needsMotive({ role: F(s, 'Role') || F(s, 'Title') || '' }, account ? F(account, 'Account Name') : '')} onChannel={setFirstChannel}
                openTasks={openTasks} stakeholderId={stakeholderId} accountId={accountIds[0] || ''}
                contact={{ firstName: F(s, 'Name') || name, name, role: F(s, 'Role') || F(s, 'Title') || '' }} company={account ? F(account, 'Account Name') : ''}
                country={F(s, 'Country') || (account ? F(account, 'Country') : '') || ''} facts={proposalFacts} note={note} statusAfter={statusAfter} busy={busy}
                onConfirm={confirmProposal} onKeep={() => setNext(n => ({ ...n, mode: 'keep' }))} />
            </div>
          )}

          <div style={section}>
            <div style={head}>Contactar</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {email && <button type="button" className="action-btn btn-primary" style={btn} disabled={!!emailSent} onClick={() => setCompose(true)}>{emailSent ? 'Correo enviado' : 'Escribir correo'}</button>}
              {phone && <a className="action-btn" style={btn} href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">Abrir WhatsApp</a>}
              {linkedin && <a className="action-btn" style={btn} href={linkedin} target="_blank" rel="noopener noreferrer">Abrir LinkedIn</a>}
              {phone && <a className="action-btn" style={btn} href={`tel:${phone}`}>Llamar</a>}
            </div>
            <div style={{ ...muted, marginTop: 4 }}>{gmail === false && email ? 'Gmail no está conectado en Configuración: el correo se abrirá en Gmail y no quedará registrado solo. ' : ''}Abrir WhatsApp, LinkedIn o el teléfono no registra nada: después contá abajo qué pasó.</div>
            {emailSent && <div role="status" style={{ fontSize: 12, marginTop: 6, color: emailLogged ? 'var(--globant-green)' : '#9A6212' }}>{emailLogged ? 'Correo enviado desde Oike y registrado en la actividad: no hace falta registrarlo otra vez.' : 'El correo salió pero no quedó registrado. No lo reenvíes: elegí «Le escribí o lo llamé» · Correo para registrarlo.'}</div>}
          </div>

          <div style={section}>
            <div style={head}>¿Qué pasó?</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {OUTCOMES.map(o => (
                <label key={o.key} style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="radio" name="outcome" checked={outcome === o.key} disabled={(o.key === 'replied' && !!selectedReply) || (o.key === 'proposal' && replyMarked)}
                    onChange={() => { setOutcome(o.key); if (!next.title || next.mode !== 'new') setNext(n => ({ ...n, title: suggestedNext(o.key, name) })); }} />
                  {o.key === 'none' && emailSent ? 'Nada más (el correo ya quedó registrado)' : o.label}
                  {o.key === 'replied' && selectedReply && <span style={muted}>(ya registrada: {fmt(F(selectedReply, 'Date'))})</span>}
                  {o.key === 'proposal' && selectedReply && !replyMarked && <span style={muted}>(se marca la respuesta del {fmt(F(selectedReply, 'Date'))}, sin registrar otra)</span>}
                  {o.key === 'proposal' && replyMarked && <span style={muted}>(ya marcada en la respuesta)</span>}
                </label>
              ))}
            </div>
            {outcome === 'contacted' && (
              <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>Por dónde
                <select className="input-field" style={{ fontSize: 12, width: 'auto' }} value={channel} onChange={e => setChannel(e.target.value)}>{CHANNELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              </label>
            )}
            {outcome === 'meeting' && <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>Cuándo <input type="datetime-local" className="input-field" style={{ fontSize: 12, width: 'auto' }} value={meetingAt} onChange={e => setMeetingAt(e.target.value)} /></label>}
            {outcome !== 'none' && <textarea className="input-field" style={{ width: '100%', minHeight: 56, marginTop: 6, fontSize: 12, boxSizing: 'border-box' }} placeholder="Nota (opcional): qué dijo, qué quedó acordado…" value={note} maxLength={2000} onChange={e => setNote(e.target.value)} />}
            {duplicateEmail && <div style={{ fontSize: 12, color: '#9A6212', marginTop: 4 }}>Ese correo ya quedó registrado al enviarlo.</div>}
          </div>

          <div style={section}>
            <div style={head}>Próximo paso</div>
            <div style={{ marginBottom: 8 }}>
              <NextActionProposal proposal={proposal?.rule === 'R0' ? null : proposal} openTasks={openTasks} stakeholderId={stakeholderId} accountId={accountIds[0] || ''}
                contact={{ firstName: F(s, 'Name') || name, name, role: F(s, 'Role') || F(s, 'Title') || '' }} company={account ? F(account, 'Account Name') : ''}
                country={F(s, 'Country') || (account ? F(account, 'Country') : '') || ''} facts={proposalFacts} note={note} statusAfter={statusAfter} busy={busy}
                onConfirm={confirmProposal} onKeep={() => setNext(n => ({ ...n, mode: 'keep' }))} />
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="radio" name="next" checked={next.mode === 'new'} disabled={openTasks.length > 0} onChange={() => setNext(n => ({ ...n, mode: 'new', title: n.title || suggestedNext(outcome, name) }))} />
                Nueva próxima acción {openTasks.length > 0 && <span style={muted}>(primero resolvé la que ya está planificada)</span>}
              </label>
              {next.mode === 'new' && (
                <div style={{ display: 'grid', gap: 6, marginLeft: 22 }}>
                  <input className="input-field" style={{ fontSize: 13 }} value={next.title} maxLength={200} onChange={e => setNext(n => ({ ...n, title: e.target.value }))} aria-label="Próxima acción" />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {DUE_SHORTCUTS.map(([k, l]) => { const d = dueFromShortcut(k, today); return <button key={k} type="button" className="action-btn" style={{ ...btn, ...(next.dueOn === d ? { background: 'var(--teal-dim)', borderColor: 'var(--globant-green)' } : {}) }} aria-pressed={next.dueOn === d} onClick={() => setNext(n => ({ ...n, dueOn: d }))}>{l}</button>; })}
                    <input type="date" className="input-field" style={{ fontSize: 12, width: 'auto' }} min={today} value={next.dueOn} onChange={e => setNext(n => ({ ...n, dueOn: e.target.value }))} aria-label="Fecha de la próxima acción" />
                    {next.dueOn && <span style={muted}>{fmtDay(next.dueOn)}</span>}
                  </div>
                </div>
              )}
              {openTasks.length > 0 && (
                <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="radio" name="next" checked={next.mode === 'keep'} onChange={() => setNext(n => ({ ...n, mode: 'keep' }))} />
                  Mantener lo planificado ({openTasks.map(t => `${t.title} · ${fmtDay(t.dueOn)}`).join('; ')})
                </label>
              )}
              <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="radio" name="next" checked={next.mode === 'none'} onChange={() => setNext(n => ({ ...n, mode: 'none' }))} />
                Sin seguimiento por ahora <span style={muted}>(no cambia el estado del contacto)</span>
              </label>
            </div>
          </div>

          {msg && <div role={msg.tone === 'error' ? 'alert' : 'status'} style={{ fontSize: 12, margin: '6px 0', color: msg.tone === 'error' || msg.tone === 'warn' ? '#9A6212' : 'var(--globant-green)' }}>{msg.text}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="action-btn btn-primary" disabled={busy || (!interaction && next.mode !== 'new')} onClick={() => save()}>{busy ? 'Guardando…' : retrying ? 'Reintentar' : 'Guardar'}</button>
            <button type="button" className="action-btn" disabled={busy} onClick={tryClose}>Cerrar sin guardar</button>
          </div>
          <div style={{ ...muted, marginTop: 6 }}>Guardar registra lo que pasó en la actividad, actualiza el estado del contacto según el resultado y deja la próxima acción en tus tareas, vinculada a este contacto. A la IA va solo lo que muestra «Qué se envía a la IA» en la propuesta; nada se envía al contacto.</div>
        </>}
      </div>
      {compose && s && (
        <div onClick={e => e.stopPropagation()}>
          <EmailComposeModal stakeholder={s} accounts={accounts} outreach={outreach} gmailConnected={!!gmail}
            onClose={() => setCompose(false)}
            onSent={(r) => { setEmailSent(r); if (!r.logged) { setOutcome('contacted'); setChannel('Email'); } }}
            onSuccess={() => { setCompose(false); commercialApi?.refresh?.(); }} />
        </div>
      )}
    </div>
  );
}
